import { Grid, Typography } from '@mui/material';
import isEqual from 'lodash.isequal';
import React, { useEffect, useState } from 'react';
import {
  useGlobalLocaleContext,
  useGlobalTimeSliderContext,
} from '../../../context/contexts';
import { format, getMomentDate, monthEnd } from '../../../date/date-utils';
import { ColoredPaper, StyledSlider } from './component-utils';
import { CustomDatePicker } from './DateRangePicker';
import { Granularity } from './types';
import { dateFormatter, displayDateRange } from './utils-display';

export const StartEndPicker = () => {
  const timeSliderHandle = useGlobalTimeSliderContext();
  const localeHandle = useGlobalLocaleContext();
  const locale = localeHandle.selected;
  const [state, dispatch] = timeSliderHandle;
  const [values, setValues] = useState<number[]>([
    state.range.all.findIndex((v) => isEqual(v, state.start)),
    state.range.all.findIndex((v) => isEqual(v, state.end)),
  ]);

  useEffect(() => {
    setValues([
      state.range.all.findIndex((v) => isEqual(v, state.start)),
      state.range.all.findIndex((v) => isEqual(v, state.end)),
    ]);
  }, [state.range, state.end, state.start]);

  const [isStartDatePickerOpen, setIsStartDatePickerOpen] =
    useState<boolean>(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] =
    useState<boolean>(false);

  const isMonthlyGranularity =
    state.selectedGranularity.value === Granularity.MONTH;

  const renderStartDatePicker = () => {
    const monthlyStartDate = state.start as number;
    const monthlyEndDate = state.end as number;
    const monthlyRangeStart = state.range.all[0] as number;
    const formattedStartDate = format(monthlyStartDate);

    return (
      <div
        style={{
          visibility: isMonthlyGranularity ? 'visible' : 'hidden',
          pointerEvents: isMonthlyGranularity ? 'auto' : 'none',
        }}
        aria-hidden={!isMonthlyGranularity}
      >
        <CustomDatePicker
          value={formattedStartDate}
          isOpen={isStartDatePickerOpen}
          toggleDatePicker={setIsStartDatePickerOpen}
          onChange={(date) => {
            if (!date) {
              return;
            }
            const formattedDate = format(monthEnd(date.valueOf()));
            const selectedDate = (state.range.all as number[]).find((v) => {
              return format(v) === formattedDate;
            });
            if (!selectedDate) {
              throw new Error(
                `Invalid state: Selected date ${formattedDate} is not in range`
              );
            }
            dispatch({
              type: 'update-range',
              start: selectedDate,
              end: state.end,
            });
          }}
          minDate={getMomentDate(monthlyRangeStart)}
          maxDate={getMomentDate(monthlyEndDate)}
        />
      </div>
    );
  };

  const renderEndDatePicker = () => {
    const monthlyStartDate = state.start as number;
    const monthlyEndDate = state.end as number;
    const monthlyRangeEnd = state.range.all[
      state.range.all.length - 1
    ] as number;
    const formattedEndDate = format(monthlyEndDate);

    return (
      <div
        style={{
          visibility: isMonthlyGranularity ? 'visible' : 'hidden',
          pointerEvents: isMonthlyGranularity ? 'auto' : 'none',
        }}
        aria-hidden={!isMonthlyGranularity}
      >
        <CustomDatePicker
          value={formattedEndDate}
          isOpen={isEndDatePickerOpen}
          toggleDatePicker={setIsEndDatePickerOpen}
          onChange={(date) => {
            if (!date) {
              return;
            }
            const formattedDate = format(monthEnd(date.valueOf()));
            const selectedDate = (state.range.all as number[]).find((v) => {
              return format(v) === formattedDate;
            });
            if (!selectedDate) {
              throw new Error(
                `Invalid state: Selected date ${formattedDate} is not in range`
              );
            }
            dispatch({
              type: 'update-range',
              start: state.start,
              end: selectedDate,
            });
          }}
          minDate={getMomentDate(monthlyStartDate)}
          maxDate={getMomentDate(monthlyRangeEnd)}
        />
      </div>
    );
  };
  return (
    <Grid
      item
      style={{
        margin: 'auto 10px',
        height: '8vh',
        width: '20vw',
        minWidth: 'fit-content',
      }}
    >
      <ColoredPaper style={{ height: '100%' }}>
        <Grid
          item
          container
          xs={12}
          style={{ paddingLeft: '0px' }}
          justifyContent="center"
          alignItems="center"
        >
          <Grid item>{renderStartDatePicker()}</Grid>
          <Grid
            item
            justifyContent="center"
            style={{
              margin: '0 4px 0 4px',
            }}
          >
            <Typography
              variant="subtitle1"
              color="secondary"
              style={{
                textAlign: 'center',
              }}
            >
              {displayDateRange(
                state.start,
                state.end,
                state.selectedGranularity.value,
                locale,
                state.timePoints
              )}
            </Typography>
          </Grid>
          <Grid item>{renderEndDatePicker()}</Grid>
        </Grid>
        <Grid
          item
          xs={12}
          container
          style={{ padding: '0px 4px' }}
          justifyContent="center"
        >
          <StyledSlider
            getAriaLabel={() => 'TimeSlider range'}
            value={values}
            onChange={(e: any, v: number | number[]) => {
              const arr = v as number[];
              setValues(arr);
            }}
            onChangeCommitted={(e: any, v: number | number[]) => {
              const [startIndex, endIndex] = v as number[];
              const start = state.range.all.at(startIndex);
              const end = state.range.all.at(endIndex);
              if (start && end) {
                dispatch({ type: 'update-range', start, end });
              } else {
                throw new Error(
                  `index ${startIndex} or ${endIndex} is out of range`
                );
              }
            }}
            valueLabelDisplay="auto"
            min={0}
            max={state.range.all.length - 1}
            getAriaValueText={(value: number) => {
              const v = state.range.all.at(value);
              return v
                ? dateFormatter(v, state.selectedGranularity.value, locale)
                : '';
            }}
            valueLabelFormat={(value: number) => {
              const v = state.range.all.at(value);
              return v
                ? dateFormatter(v, state.selectedGranularity.value, locale)
                : '';
            }}
          />
        </Grid>
      </ColoredPaper>
    </Grid>
  );
};
